import React, { useEffect, useState, useCallback } from 'react';
import { Navbar } from '../components/Navbar';
import { 
  Video, Plus, Edit2, Trash2, Eye, EyeOff, Loader, 
  Star, BarChart3, Clock, Users, Filter
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const VISIBILITY_OPTIONS = {
  premium: "Todos os Vendedores (Premium+)",
  pro: "Pro e superiores (Pro+)",
  advanced: "Advanced e superiores (Advanced+)"
};

export const AdminCourses = () => {
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState({});
  const [tiers, setTiers] = useState({});
  const [stats, setStats] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [filterCategory, setFilterCategory] = useState('');
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    video_url: '',
    thumbnail_url: '',
    duration_minutes: '',
    min_plan: 'premium',
    order: 0,
    is_featured: false
  });

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/courses/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  }, []);

  const fetchTiers = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/courses/tiers`);
      setTiers(response.data);
    } catch (error) {
      console.error('Erro ao carregar tiers:', error);
    }
  }, []);

  const fetchCourses = useCallback(async () => {
    try {
      const params = filterCategory ? `?category=${filterCategory}` : '';
      const response = await axios.get(`${API_URL}/admin/courses${params}`, {
        withCredentials: true
      });
      setCourses(response.data.courses);
    } catch (error) {
      toast.error('Erro ao carregar cursos');
    }
  }, [filterCategory]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/courses/stats`, {
        withCredentials: true
      });
      setStats(response.data);
    } catch (error) {
      console.error('Erro ao carregar stats:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchCategories(),
        fetchTiers(),
        fetchCourses(),
        fetchStats()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchCategories, fetchTiers, fetchCourses, fetchStats]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        ...formData,
        duration_minutes: formData.duration_minutes ? parseInt(formData.duration_minutes) : null,
        order: parseInt(formData.order)
      };

      if (editingCourse) {
        await axios.put(
          `${API_URL}/admin/courses/${editingCourse.course_id}`,
          payload,
          { withCredentials: true }
        );
        toast.success('Curso atualizado!');
      } else {
        await axios.post(`${API_URL}/admin/courses`, payload, {
          withCredentials: true
        });
        toast.success('Curso criado!');
      }

      resetForm();
      fetchCourses();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar curso');
    }
  };

  const resetForm = () => {
    setIsDialogOpen(false);
    setEditingCourse(null);
    setFormData({
      title: '',
      description: '',
      category: '',
      video_url: '',
      thumbnail_url: '',
      duration_minutes: '',
      min_plan: 'premium',
      order: 0,
      is_featured: false
    });
  };

  const handleEdit = (course) => {
    setEditingCourse(course);
    setFormData({
      title: course.title,
      description: course.description,
      category: course.category,
      video_url: course.video_url,
      thumbnail_url: course.thumbnail_url || '',
      duration_minutes: course.duration_minutes || '',
      min_plan: course.min_plan || 'premium',
      order: course.order || 0,
      is_featured: course.is_featured
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (courseId) => {
    if (!window.confirm('Tem certeza que deseja excluir este curso?')) return;
    
    try {
      await axios.delete(`${API_URL}/admin/courses/${courseId}`, {
        withCredentials: true
      });
      toast.success('Curso excluído!');
      fetchCourses();
      fetchStats();
    } catch (error) {
      toast.error('Erro ao excluir curso');
    }
  };

  const handleTogglePublish = async (course) => {
    try {
      await axios.put(
        `${API_URL}/admin/courses/${course.course_id}`,
        { is_published: !course.is_published },
        { withCredentials: true }
      );
      toast.success(course.is_published ? 'Curso despublicado' : 'Curso publicado!');
      fetchCourses();
    } catch (error) {
      toast.error('Erro ao alterar status');
    }
  };

  const handleToggleFeatured = async (course) => {
    try {
      await axios.put(
        `${API_URL}/admin/courses/${course.course_id}`,
        { is_featured: !course.is_featured },
        { withCredentials: true }
      );
      toast.success(course.is_featured ? 'Destaque removido' : 'Curso destacado!');
      fetchCourses();
    } catch (error) {
      toast.error('Erro ao alterar destaque');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <Loader className="w-12 h-12 animate-spin text-indigo-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2" data-testid="admin-courses-title">
              Cursos Needi
            </h1>
            <p className="text-lg text-gray-600">
              Gerencie os cursos disponíveis para vendedores
            </p>
          </div>
          <Button
            onClick={() => {
              resetForm();
              setIsDialogOpen(true);
            }}
            className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
            data-testid="add-course-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Novo Curso
          </Button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total de Cursos</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{stats.total_courses}</p>
                </div>
                <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                  <Video className="w-6 h-6 text-indigo-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Publicados</p>
                  <p className="text-2xl font-bold text-green-600 mt-1">{stats.published_courses}</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <Eye className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Destacados</p>
                  <p className="text-2xl font-bold text-amber-600 mt-1">{stats.featured_courses}</p>
                </div>
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Star className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Views</p>
                  <p className="text-2xl font-bold text-purple-600 mt-1">{stats.total_views}</p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filter */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex items-center gap-4">
            <Filter className="w-4 h-4 text-gray-500" />
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="h-9 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              data-testid="filter-category"
            >
              <option value="">Todas as categorias</option>
              {categories.categories?.map(cat => (
                <option key={cat} value={cat}>
                  {categories.labels?.[cat]}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Courses List */}
        {courses.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <Video className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Nenhum curso ainda</h3>
            <p className="text-gray-600 mb-6">Comece adicionando seu primeiro curso!</p>
            <Button onClick={() => setIsDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Adicionar Curso
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {courses.map((course) => (
              <div
                key={course.course_id}
                className={`bg-white rounded-xl shadow-sm border overflow-hidden hover:shadow-md transition-shadow ${
                  !course.is_published ? 'opacity-60' : ''
                }`}
                data-testid={`course-${course.course_id}`}
              >
                {/* Thumbnail */}
                {course.thumbnail_url ? (
                  <div className="aspect-video w-full overflow-hidden bg-gray-100">
                    <img
                      src={course.thumbnail_url}
                      alt={course.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="aspect-video w-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                    <Video className="w-12 h-12 text-white opacity-50" />
                  </div>
                )}
                
                <div className="p-5">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-gray-900 line-clamp-1">{course.title}</h3>
                    <div className="flex items-center gap-1">
                      {course.is_featured && (
                        <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                      )}
                      {!course.is_published && (
                        <EyeOff className="w-4 h-4 text-gray-400" />
                      )}
                    </div>
                  </div>
                  
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                    {course.description}
                  </p>

                  <div className="flex flex-wrap gap-2 mb-4">
                    <span className="text-xs px-2 py-1 bg-indigo-100 text-indigo-700 rounded">
                      {categories.labels?.[course.category]}
                    </span>
                    <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded">
                      {VISIBILITY_OPTIONS[course.min_plan] || `Tier ${course.min_tier}+`}
                    </span>
                    {course.duration_minutes && (
                      <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {course.duration_minutes}min
                      </span>
                    )}
                  </div>

                  <div className="text-xs text-gray-500 mb-4">
                    {course.views} visualizações
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(course)}
                      data-testid={`edit-course-${course.course_id}`}
                    >
                      <Edit2 className="w-3 h-3 mr-1" />
                      Editar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleTogglePublish(course)}
                    >
                      {course.is_published ? (
                        <><EyeOff className="w-3 h-3 mr-1" /> Despublicar</>
                      ) : (
                        <><Eye className="w-3 h-3 mr-1" /> Publicar</>
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggleFeatured(course)}
                      className={course.is_featured ? 'text-amber-600' : ''}
                    >
                      <Star className={`w-3 h-3 ${course.is_featured ? 'fill-amber-500' : ''}`} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(course.course_id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-course-${course.course_id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Course Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle data-testid="course-dialog-title">
              {editingCourse ? 'Editar Curso' : 'Novo Curso'}
            </DialogTitle>
            <DialogDescription>
              Preencha as informações do curso
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Título *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Ex: Como criar anúncios que vendem"
                required
                data-testid="course-title-input"
              />
            </div>
            
            <div>
              <Label>Descrição *</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Descreva o conteúdo do curso..."
                rows={3}
                required
                data-testid="course-description-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Categoria *</Label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full h-10 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  required
                  data-testid="course-category-select"
                >
                  <option value="">Selecione...</option>
                  {categories.categories?.map(cat => (
                    <option key={cat} value={cat}>
                      {categories.labels?.[cat]}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Disponível para *</Label>
                <select
                  value={formData.min_plan}
                  onChange={(e) => setFormData(prev => ({ ...prev, min_plan: e.target.value }))}
                  className="w-full h-10 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  data-testid="course-visibility-select"
                >
                  {Object.entries(VISIBILITY_OPTIONS).map(([key, label]) => (
                    <option key={key} value={key}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            <div>
              <Label>URL do Vídeo *</Label>
              <Input
                type="url"
                value={formData.video_url}
                onChange={(e) => setFormData(prev => ({ ...prev, video_url: e.target.value }))}
                placeholder="https://youtube.com/watch?v=..."
                required
                data-testid="course-video-input"
              />
            </div>

            <div>
              <Label>URL da Thumbnail (opcional)</Label>
              <Input
                type="url"
                value={formData.thumbnail_url}
                onChange={(e) => setFormData(prev => ({ ...prev, thumbnail_url: e.target.value }))}
                placeholder="https://..."
                data-testid="course-thumbnail-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Duração (minutos)</Label>
                <Input
                  type="number"
                  value={formData.duration_minutes}
                  onChange={(e) => setFormData(prev => ({ ...prev, duration_minutes: e.target.value }))}
                  placeholder="30"
                  min="1"
                  data-testid="course-duration-input"
                />
              </div>
              <div>
                <Label>Ordem</Label>
                <Input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                  placeholder="0"
                  min="0"
                  data-testid="course-order-input"
                />
              </div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_featured}
                onChange={(e) => setFormData(prev => ({ ...prev, is_featured: e.target.checked }))}
                className="rounded"
              />
              <span className="text-sm">Destacar este curso</span>
            </label>

            <div className="flex gap-2 pt-4">
              <Button type="submit" className="flex-1" data-testid="course-submit-btn">
                {editingCourse ? 'Atualizar' : 'Criar'} Curso
              </Button>
              <Button type="button" variant="outline" onClick={resetForm}>
                Cancelar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
